import {AppShell, Center, Loader, Text, createStyles} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React, {useCallback, useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {MODEL_HUB_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import {redirectToModelHubSignup} from 'model-hub/modelHubSignupRedirect';
import EntityStore from 'stores/EntityStore';
import isLikelyMobileCapabilities from 'util/isLikelyMobileCapabilities';

import AccountPage from './AccountPage';
import BatchDetailPage from './BatchDetailPage';
import BatchesPage from './BatchesPage';
import ContactUsModal from './contact-us/ContactUsModal';
import {
  MODEL_HUB_MAIN_PADDING_X_PX,
  MODEL_HUB_MAIN_PADDING_Y_PX,
  MODEL_HUB_NAVBAR_WIDTH_PX,
} from './ModelHubConstants';
import ModelHubContent from './ModelHubContent';
import ModelHubDesktopPrompt from './ModelHubDesktopPrompt';
import ModelHubNavbar from './ModelHubNavbar';
import type {ModelHubPage} from './ModelHubPages';
import {
  activePageForRoute,
  batchDetailPath,
  batchesPath,
  modelsPath,
  newPredictionPath,
  pathForPage,
  pathForRoute,
  predictionDetailPath,
  predictionsPath,
  type ModelHubRoute,
} from './modelHubRoutes';
import NewPredictionPage from './new-prediction/NewPredictionPage';
import PredictionDetailPage from './PredictionDetailPage';
import PredictionsPage from './PredictionsPage';
import {requireScientificModel, type ScientificModelNode} from './scientific-models/ScientificModelMetadata';
import {
  ScientificModelRegistry,
  useScientificModelRegistry,
} from './scientific-models/useAvailableScientificModels';
import ModelHubSignup from './signup/ModelHubSignup';

interface ModelHubAppProps {
  route: ModelHubRoute;
}

// Routes a signed-out user may VIEW. Everything else is gated to signup. The models list is
// non-sensitive static metadata that the GraphQL query serves without a session; user-specific
// pages (predictions/batches/details) are not viewable signed-out.
const SIGNED_OUT_VIEWABLE_ROUTES: ReadonlySet<ModelHubRoute['kind']> = new Set(['models', 'signup']);
const CONTINUED_ON_MOBILE_SESSION_STORAGE_KEY = 'model-hub-continued-on-mobile';

export default function ModelHubApp({route}: ModelHubAppProps): JSX.Element {
  const [continuedOnMobile, setContinuedOnMobile] = useState(
    () => sessionStorage.getItem(CONTINUED_ON_MOBILE_SESSION_STORAGE_KEY) === 'true'
  );
  const handleContinueOnMobile = (): void => {
    sessionStorage.setItem(CONTINUED_ON_MOBILE_SESSION_STORAGE_KEY, 'true');
    setContinuedOnMobile(true);
  };

  // Model Hub's tables and 3D viewers assume a desktop-sized window. Warn likely-mobile devices,
  // including on /signup, while keeping false positives to one click of friction.
  if (isLikelyMobileCapabilities() && !continuedOnMobile) {
    return <ModelHubDesktopPrompt onContinue={handleContinueOnMobile} />;
  }

  // The signup wizard is a signed-out takeover served at /signup; it renders its own full-screen
  // flow instead of the app chrome.
  if (route.kind === 'signup') {
    return <ModelHubSignup />;
  }
  // Signed-out users may view the models list, but any other route bounces to signup. (The backend
  // only renders the shell for `/` and `/models` when signed out, so this is mostly defensive
  // against client-side navigation into a gated route.)
  const isSignedIn = EntityStore.currentUser() != null;
  if (!isSignedIn && !SIGNED_OUT_VIEWABLE_ROUTES.has(route.kind)) {
    return <SignupRedirect route={route} />;
  }
  return <AuthenticatedModelHubApp isSignedIn={isSignedIn} route={route} />;
}

function SignupRedirect({route}: {route: ModelHubRoute}): JSX.Element {
  useEffect(() => {
    redirectToModelHubSignup(pathForRoute(route));
  }, [route]);
  return (
    <Center h="100vh">
      <Loader size="md" />
    </Center>
  );
}

function AuthenticatedModelHubApp({
  isSignedIn,
  route,
}: {
  isSignedIn: boolean;
  route: ModelHubRoute;
}): JSX.Element {
  const result = useScientificModelRegistry();
  if (result.type === 'loading') {
    return (
      <Center h="100vh">
        <Loader size="md" />
      </Center>
    );
  }
  if (result.type === 'error') {
    return (
      <Center h="100vh">
        <Text color="red">Error loading models: {result.error.message}</Text>
      </Center>
    );
  }
  return <LoadedModelHubApp isSignedIn={isSignedIn} registry={result.registry} route={route} />;
}

function LoadedModelHubApp({
  isSignedIn,
  registry,
  route,
}: {
  isSignedIn: boolean;
  registry: ScientificModelRegistry;
  route: ModelHubRoute;
}): JSX.Element {
  const {classes} = useStyles();
  const [contactUsOpened, {open: openContactUs, close: closeContactUs}] = useDisclosure(false);
  const availableStructurePredictionModels = registry.availableStructurePredictionModels;

  // Both entry points (navbar footer link, page-header button) open this one modal instance, so the
  // source is recorded on the open event to tell which affordance people actually use.
  const handleContactUs = useCallback(
    (source: 'navbar' | 'page_header') => {
      trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.CONTACT_US_OPENED, {source});
      openContactUs();
    },
    [openContactUs]
  );

  const handleContactUsFromNavbar = useCallback(() => {
    handleContactUs('navbar');
  }, [handleContactUs]);

  // Signed-out users have no session for the feedback endpoint (`@logged_in`), so the page-header
  // trigger is withheld entirely rather than bouncing to signup.
  const handleContactUsFromPageHeader = isSignedIn
    ? () => {
        handleContactUs('page_header');
      }
    : undefined;

  // Any interaction beyond viewing the models list requires a session. When signed out, gated
  // actions bounce to /signup instead of navigating within the app.
  const guardedNavigate = useCallback(
    (path: string) => {
      if (isSignedIn) {
        navigateToURL(path);
      } else {
        trackEvent(MODEL_HUB_ANALYTICS_ACTIONS.AUTH_REDIRECTED_TO_SIGN_UP_FOR_GATED_ACTION);
        redirectToModelHubSignup(path);
      }
    },
    [isSignedIn]
  );

  // Navigation is URL-driven: each handler pushes a history entry via navigateToURL, and the
  // Backbone router re-renders this component with the matching route. Browser Back/Forward work
  // for free. Navigating to the models list is open; everything else is gated via guardedNavigate.
  const handleNavigate = useCallback(
    (page: ModelHubPage) => {
      if (page === 'models') {
        navigateToURL(pathForPage(page));
      } else {
        guardedNavigate(pathForPage(page));
      }
    },
    [guardedNavigate]
  );

  const handleNewPrediction = useCallback(
    (model?: ScientificModelNode) => {
      guardedNavigate(newPredictionPath(model?.externalReference));
    },
    [guardedNavigate]
  );

  // Detail views fetch their own name from the resource, so a row click just navigates to the
  // canonical URL. The listing's `name` arg is unused here (kept in the callback contract for the
  // row-selection handlers).
  const handleSelectPrediction = useCallback(
    (predictionId: string) => {
      guardedNavigate(predictionDetailPath(predictionId));
    },
    [guardedNavigate]
  );

  const handleSelectBatch = useCallback(
    (batchId: string) => {
      guardedNavigate(batchDetailPath(batchId));
    },
    [guardedNavigate]
  );

  const handleBackToBatches = useCallback(() => {
    guardedNavigate(batchesPath());
  }, [guardedNavigate]);

  // Seeds the batch filter as a query param on /predictions; PredictionsPage reads it from the URL.
  const handleViewPredictions = useCallback(
    (batchId: string) => {
      guardedNavigate(predictionsPath(batchId));
    },
    [guardedNavigate]
  );

  // New prediction is a full-screen takeover: render it outside AppShell so there's no navbar or
  // main-region padding. AppShell here is pure layout chrome — the app-wide providers
  // (Redux/Apollo/theme) live above ModelHubApp, so nothing is lost by bypassing it.
  if (route.kind === 'new-prediction') {
    return (
      <NewPredictionPage
        availableModels={availableStructurePredictionModels}
        onBack={() => {
          navigateToURL(modelsPath());
        }}
        onSubmitted={() => {
          navigateToURL(batchesPath());
        }}
        preselectedModel={
          route.model == null
            ? undefined
            : requireScientificModel(registry.catalogByExternalReference, route.model)
        }
      />
    );
  }

  const renderPredictionDetail = (id: string): JSX.Element => (
    <PredictionDetailPage
      catalogByExternalReference={registry.catalogByExternalReference}
      onNavigateToBatch={(batchId) => {
        navigateToURL(batchDetailPath(batchId));
      }}
      onNavigateToBatches={handleBackToBatches}
      onNavigateToPredictions={() => {
        navigateToURL(predictionsPath());
      }}
      predictionId={id}
    />
  );

  return (
    <>
      <AppShell
        classNames={{main: classes.main}}
        navbar={
          <ModelHubNavbar
            activePage={activePageForRoute(route) ?? 'models'}
            onContactUs={handleContactUsFromNavbar}
            onNavigate={handleNavigate}
            onNewPrediction={() => {
              handleNewPrediction();
            }}
          />
        }
        padding="md"
      >
        {route.kind === 'models' && (
          <ModelHubContent
            models={registry.availableModels}
            onContactUs={handleContactUsFromPageHeader}
            onNewPrediction={handleNewPrediction}
          />
        )}
        {route.kind === 'predictions' && (
          <PredictionsPage
            catalogByExternalReference={registry.catalogByExternalReference}
            models={registry.availableModels}
            onContactUs={handleContactUsFromPageHeader}
            onNewPrediction={() => {
              handleNewPrediction();
            }}
            onSelectPrediction={handleSelectPrediction}
          />
        )}
        {route.kind === 'prediction-detail' && renderPredictionDetail(route.id)}
        {route.kind === 'batches' && (
          <BatchesPage
            catalogByExternalReference={registry.catalogByExternalReference}
            onContactUs={handleContactUsFromPageHeader}
            onNewPrediction={() => {
              handleNewPrediction();
            }}
            onSelectBatch={handleSelectBatch}
          />
        )}
        {route.kind === 'batch-detail' && (
          <BatchDetailPage
            batchId={route.id}
            catalogByExternalReference={registry.catalogByExternalReference}
            onBackToBatches={handleBackToBatches}
            onSelectPrediction={handleSelectPrediction}
            onViewPredictions={handleViewPredictions}
          />
        )}
        {route.kind === 'account' && <AccountPage />}
      </AppShell>
      {/* Portaled, so it lives outside AppShell to stay clear of the main region's padding. */}
      {isSignedIn && <ContactUsModal onClose={closeContactUs} opened={contactUsOpened} />}
    </>
  );
}

const useStyles = createStyles((theme) => ({
  main: {
    '&&': {
      backgroundColor: theme.white,
      padding: `${MODEL_HUB_MAIN_PADDING_Y_PX}px ${MODEL_HUB_MAIN_PADDING_X_PX}px`,
      paddingLeft: MODEL_HUB_NAVBAR_WIDTH_PX + MODEL_HUB_MAIN_PADDING_X_PX,
    },
  },
}));
